import * as Sentry from "@sentry/vue";

type AuthTimeoutDiagnostics = {
  online: boolean;
  controlledByServiceWorker: boolean;
  serviceWorkers: { scope: string; scriptURL?: string }[] | string;
  cacheNames: string[] | string;
};

const DIAGNOSTIC_LOOKUP_TIMEOUT_MS = 1000;

// A broken service worker state can leave these lookups pending forever, blocking the report
function withDeadline<T>(promise: Promise<T>): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(
        () => reject(new Error("Diagnostic lookup timed out")),
        DIAGNOSTIC_LOOKUP_TIMEOUT_MS
      )
    ),
  ]);
}

async function collectDiagnostics(): Promise<AuthTimeoutDiagnostics> {
  const diagnostics: AuthTimeoutDiagnostics = {
    online: navigator.onLine,
    controlledByServiceWorker: !!navigator.serviceWorker?.controller,
    serviceWorkers: [],
    cacheNames: [],
  };

  try {
    const registrations = navigator.serviceWorker
      ? await withDeadline(navigator.serviceWorker.getRegistrations())
      : [];
    diagnostics.serviceWorkers = registrations.map((registration) => ({
      scope: registration.scope,
      scriptURL: (
        registration.active ||
        registration.waiting ||
        registration.installing
      )?.scriptURL,
    }));
  } catch (error) {
    diagnostics.serviceWorkers = String(error);
  }

  try {
    diagnostics.cacheNames = window.caches
      ? await withDeadline(window.caches.keys())
      : [];
  } catch (error) {
    diagnostics.cacheNames = String(error);
  }

  return diagnostics;
}

// Awaited before rethrowing so Sentry flags the error and skips the generic Vue capture
export async function reportAuthTimeout(
  error: unknown,
  step: string
): Promise<void> {
  Sentry.captureException(error, {
    tags: { auth_timeout_step: step },
    contexts: { auth_timeout: await collectDiagnostics() },
  });
}
